import { logException } from 'App/logging';
import { useEffect, useState } from 'react';
import {
  getConversationSummaryConversationSummaryGetResponseError,
  getConversationSummaryConversationSummaryGet,
} from 'Assistant/__generated__/client';
import { useAuthHeader } from 'Assistant/Chatbot/hooks/useAuthHeader';

/**
 * Statuses that don't need reporting from here: 401/403 mean the app is
 * working correctly (the user's session or entitlement isn't valid), and a
 * 503 is already reported server-side with a stack trace we don't have, so
 * logging it again here would just be noise.
 */
const EXPECTED_STATUSES = [401, 403, 503];

const isReportableStatus = (
  status: getConversationSummaryConversationSummaryGetResponseError['status'],
) => !EXPECTED_STATUSES.includes(status);

export interface ConversationSummary {
  onboardingStatus: string;
  onboardingCompletedAt: Date | null;
  lastMessageAt: Date | null;
}

export const useConversationSummary = () => {
  const [summary, setSummary] = useState<ConversationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const accessTokenHeader = useAuthHeader();

  useEffect(() => {
    if (!accessTokenHeader) {
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fetchConversationSummary = async () => {
      try {
        const { data, status } =
          await getConversationSummaryConversationSummaryGet({
            headers: {
              'client-platform': 'web',
              ...accessTokenHeader,
            },
          });

        if (cancelled) {
          return;
        }

        if (status !== 200) {
          if (isReportableStatus(status)) {
            logException(
              new Error(
                'Unexpected response fetching nova conversation summary',
              ),
              {
                tags: {
                  product: 'nova',
                  feature: 'nova-homepage-todo-list-item',
                  action: 'fetch-conversation-summary',
                },
                extra: { status },
              },
            );
          }

          return;
        }

        setSummary({
          onboardingStatus: data.onboarding_status,
          onboardingCompletedAt: data.onboarding_completed_at
            ? new Date(data.onboarding_completed_at)
            : null,
          lastMessageAt: data.last_message_at
            ? new Date(data.last_message_at)
            : null,
        });
      } catch (ex) {
        if (!cancelled) {
          logException(ex, {
            tags: {
              product: 'nova',
              feature: 'nova-homepage-todo-list-item',
              action: 'fetch-conversation-summary',
            },
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchConversationSummary();

    return () => {
      cancelled = true;
    };
  }, [accessTokenHeader]);

  return { summary, loading };
};
